import { useState } from "react";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import useSWR from "swr";
import { AnimatePresence, motion } from "framer-motion";
import { FireIcon, LockClosedIcon } from "@heroicons/react/20/solid";
import { CalendarIcon } from "@heroicons/react/24/outline";
import {
  ExclamationTriangleIcon,
  FaceFrownIcon,
} from "@heroicons/react/24/solid";
import { EventData, EventShowingsMsgs } from "@types";
import { Basket } from "@features/cartDetails/types";
import ShowingCountDown from "@features/eventPage/components/showingCountDown";
import { fetcher } from "@utils/fetchers";
import { useLatestCookieValue } from "@utils/hooks";
import { AccessType, ShowingStatus } from "@utils/enums";
import {
  getCookieConsent,
  getCookieValue,
  joinClassNames,
} from "@utils/helpers";
import {
  useIsShopsite,
  useRequiresCookieConsent,
} from "@context/DomainContext";
import { bookedShowingTickets, handleShowingType } from "../utils/helpers";
import ConnectedShowings from "../components/connectedShowings";
import {
  Badge,
  Card,
  Flex,
  Heading,
  Link,
  Text,
  Button,
} from "@components/theming";
import { EventAreaPicker } from "@features/eventAreaPicker";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/accordion";
import ShowingStatusToolTip from "../components/showingStatusToolTip";
import SkeletonLoading from "../components/skeletonLoading";
import { AreaFormatted } from "@features/eventAreaPicker/types";

interface Props {
  getPriceRange: ({
    pricesectionIds,
    groupedPricesectionIds,
  }: {
    pricesectionIds?: number[];
    groupedPricesectionIds?: number[];
  }) => { min: number; max: number; isEmpty?: boolean };
  event: EventData;
}

const EventListMode = ({ getPriceRange, event }: Props) => {
  const t = useTranslations("EventPage");
  const utilTranslations = useTranslations("Utils");
  const statusTranslations = useTranslations("EventPage.statuses");
  const basketKey = useLatestCookieValue("ASP.NET_SessionId");
  const params = useSearchParams();
  const isShopsite = useIsShopsite();
  const promo = params.get("promo");
  const eventId = event.legacy.e_id;
  const eventVenueId = event.legacy.ev_id;
  const [requestingBasket, setRequestingBasket] = useState(false);
  // if user does not consent to cookies, don't pass analytics configs down to children
  // so analytics calls won't accidentally be triggered
  const cookieConsentRequired = useRequiresCookieConsent();
  const consent = getCookieConsent();
  const [lastAreaSelected, setLastAreaSelected] =
    useState<AreaFormatted | null>(null);

  const {
    data: showingData,
    isLoading,
    mutate,
    error,
  } = useSWR<EventShowingsMsgs>(
    event.status === "published"
      ? `events/${eventId}/${eventVenueId}/showings${
          promo ? `?promoCode=${promo}` : ""
        }`
      : null,
    fetcher
  );

  const { data: basket } = useSWR<Basket>(
    basketKey ? `basket/${basketKey}` : null,
    fetcher
  );

  async function createBasket() {
    const basketKey = getCookieValue("ASP.NET_SessionId");
    if (!requestingBasket && !basketKey) {
      setRequestingBasket(true);
      const session_params = getCookieValue("itk_sessionparams") ?? "";
      const res = await fetch(`/api/basket`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(session_params),
      });
      if (!res.ok) {
        const retry_after = res.headers.get("retry-after") ?? "";
        setTimeout(() => createBasket(), parseInt(retry_after) * 1000);
      }
      setRequestingBasket(!res.ok);
    }
  }

  const basketEvent = basket?.Events?.find((event) => event.Id === eventId);

  const activeShowings = showingData?.showings
    .filter((showing) => showing.active)
    .sort((a, b) => a.order - b.order);

  return error && error.request?.status !== 404 ? (
    <div
      className={joinClassNames(
        "relative overflow-hidden",
        isShopsite ? "mt-4 sm:mt-8 border border-gray-200 rounded-lg" : "mt-8"
      )}
    >
      <div className="flex flex-col items-center justify-center w-full py-8">
        <div className="relative">
          <Heading
            as="h2"
            size="8"
            weight="bold"
            sharedClasses="relative z-10"
            shopsiteClasses="font-black text-4xl"
          >
            {utilTranslations("oops")}
          </Heading>
          <ExclamationTriangleIcon
            className={joinClassNames(
              "absolute -top-16 -left-16 w-24 h-24 -rotate-12",
              isShopsite ? "text-red-400" : "text-[color:var(--red-a6)]"
            )}
          />
        </div>
        <div className="relative mx-4">
          <Heading
            as="h3"
            size="6"
            weight="bold"
            align="center"
            sharedClasses="relative z-10"
            shopsiteClasses="font-bold text-2xl text-center"
          >
            {utilTranslations("anerroroccurred")}
          </Heading>
        </div>
        <div className="relative text-center mb-4">
          <Text
            as="p"
            size="4"
            color="gray"
            className="relative z-10"
            shopsiteClasses="text-gray-500 text-lg"
          >
            {utilTranslations("dontworrytryagain")}
          </Text>
          <Text
            as="p"
            size="4"
            color="gray"
            className="relative z-10"
            shopsiteClasses="text-gray-500 text-lg"
          >
            {utilTranslations.rich("otherwisecontactsupport", {
              button: (chunks) => (
                <Link
                  href={"/contact"}
                  weight="medium"
                  sharedClasses="underline"
                  shopsiteClasses="text-black font-semibold"
                >
                  {chunks}
                </Link>
              ),
            })}
          </Text>
          <FaceFrownIcon
            className={joinClassNames(
              "absolute -bottom-8 -right-2 sm:-right-[4.25rem] w-24 h-24 rotate-[15deg] z-0",
              isShopsite ? "text-[#F9CF47]" : "text-[color:var(--yellow-a6)]"
            )}
          />
        </div>
        <Button onClick={() => window?.location.reload()}>
          {utilTranslations("tryagain")}
        </Button>
      </div>
    </div>
  ) : activeShowings && activeShowings.length > 0 ? (
    <div
      className={joinClassNames(
        "flex flex-col gap-5",
        isShopsite && event.subdescription ? "mt-10" : "mt-4"
      )}
    >
      {activeShowings.length > 1 ? (
        <Heading
          as="h2"
          size="4"
          weight="bold"
          shopsiteClasses="text-lg font-semibold"
        >
          {t("choosedate")}
        </Heading>
      ) : (
        <></>
      )}
      <Accordion
        type="single"
        defaultValue={
          activeShowings.length === 1
            ? activeShowings[0].id.toString()
            : undefined
        }
        disabled={activeShowings.length === 1}
        collapsible={activeShowings.length > 1}
        asChild
        sharedClasses="overflow-hidden sm:border"
        shopsiteClasses={joinClassNames(
          "bg-white max-sm:-mx-4 sm:rounded-xl",
          activeShowings.length === 1 ? "-mb-4" : ""
        )}
        whitelabelClasses="border-[color:var(--gray-5)] max-sm:-mx-6 sm:rounded-[var(--radius-4)]"
      >
        <motion.div
          data-testid="showinglistings"
          initial="hidden"
          animate="show"
          exit="hidden"
          variants={{
            hidden: {
              opacity: 0,
            },
            show: {
              opacity: 1,
              transition: {
                staggerChildren: 0.05,
              },
            },
          }}
        >
          {activeShowings.map((showing, index) => {
            const showingConfig = handleShowingType(
              showing,
              showingData?.messages,
              statusTranslations
            );
            const isSelectable = showingConfig?.selectable;
            const bookedTickets = bookedShowingTickets(showing.id, basketEvent);

            if (!isSelectable) {
              return (
                <motion.div
                  key={showing.id}
                  className={joinClassNames(
                    "relative flex flex-row items-center gap-4 px-4 sm:px-6 py-5 w-full border-b",
                    index === 0 ? "max-sm:!border-t" : "",
                    index === activeShowings.length - 1 && isShopsite
                      ? "sm:!border-b-0"
                      : index === activeShowings.length - 1
                      ? "!border-b-0"
                      : "",
                    isShopsite
                      ? "bg-gray-100"
                      : "border-[color:var(--gray-5)] bg-[color:var(--gray-a3)]"
                  )}
                  variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }}
                >
                  <div className="flex items-start gap-x-4 w-full">
                    <div className="relative flex items-center justify-center w-8 h-5 -mt-0.5">
                      <CalendarIcon
                        className={joinClassNames(
                          "h-6 w-6 flex-none rounded bg-transparent",
                          isShopsite ? "" : "text-[color:var(--gray-11)]"
                        )}
                      />
                      {showing.status.id === ShowingStatus.SOLDOUT ||
                      showing.status.id === ShowingStatus.ADMINSOLDOUT ? (
                        <span
                          style={{ fontSize: "0.5rem" }}
                          className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 leading-none bg-red-400 text-white pt-0.5 pb-px px-0.5 w-max -rotate-6"
                        >
                          {t("soldout")}
                        </span>
                      ) : showing.access.type === AccessType.PASSWORD ? (
                        <LockClosedIcon
                          className={joinClassNames(
                            "absolute -bottom-1 -right-1 h-4 w-4 stroke-2 text-yellow-500",
                            isShopsite
                              ? "bg-gray-100"
                              : "bg-[color:var(--gray-5)]"
                          )}
                        />
                      ) : showing.status.hot ? (
                        <FireIcon
                          className={joinClassNames(
                            "absolute -bottom-1 -right-1 h-4 w-4 stroke-2 text-orange-400",
                            isShopsite
                              ? "bg-gray-100"
                              : "bg-[color:var(--gray-5)]"
                          )}
                        />
                      ) : (
                        <></>
                      )}
                    </div>
                    <div className="flex flex-row items-start justify-between flex-wrap gap-x-4 gap-y-3.5 w-full">
                      <div className="text-left">
                        <Text
                          as="p"
                          size="2"
                          weight="bold"
                          whitelabelClasses="text-[color:var(--gray-11)]"
                          shopsiteClasses="text-sm font-semibold"
                        >
                          {showing.date.display
                            ? showing.date.display
                            : showing.date.local.formatted}
                        </Text>
                        {showing.name ? (
                          <Text
                            as="p"
                            size="2"
                            weight="bold"
                            sharedClasses="w-fit mt-1 h-4"
                            whitelabelClasses="text-[color:var(--accent-8)]"
                            shopsiteClasses="text-xs leading-5 text-gray-500"
                          >
                            {showing.name}
                          </Text>
                        ) : (
                          <></>
                        )}
                      </div>
                      <Badge
                        size="3"
                        sharedClasses="-mt-1.5 -ml-2"
                        shopsiteClasses="gap-1 !py-1.5 !rounded-md whitespace-nowrap font-medium"
                        color={
                          showingConfig.color as
                            | "red"
                            | "green"
                            | "orange"
                            | undefined
                        }
                      >
                        {showing.status.id === ShowingStatus.AVAILABLE &&
                        showing.access.type === AccessType.NOTYETONSALE ? (
                          <ShowingCountDown
                            ttl={showing.access.date.public.secondstostart}
                            title={showingConfig?.title ?? ""}
                            mutate={mutate}
                            textColor={
                              isShopsite
                                ? "text-orange-600"
                                : "text-[color:var(--orange-a11)]"
                            }
                            loadingColor={
                              isShopsite ? "#ea580c" : "var(--orange-a11)"
                            }
                          />
                        ) : (
                          <Text
                            as="span"
                            shopsiteClasses={showingConfig?.textColor}
                          >
                            {showingConfig?.title}
                          </Text>
                        )}
                        {showingConfig.description?.trim() ? (
                          <ShowingStatusToolTip
                            statusId={showing.status.id}
                            accessType={showing.access.type}
                            title={showingConfig.title ?? ""}
                            description={showingConfig.description ?? ""}
                          />
                        ) : (
                          <></>
                        )}
                      </Badge>
                    </div>
                  </div>
                </motion.div>
              );
            }

            return (
              <AccordionItem
                asChild
                key={showing.id}
                value={showing.id.toString()}
                className={joinClassNames(
                  index === 0 ? "max-sm:!border-t" : "",
                  index === activeShowings.length - 1 &&
                    isShopsite &&
                    activeShowings.length > 1
                    ? "sm:!border-b-0"
                    : index === activeShowings.length - 1
                    ? "!border-b-0"
                    : ""
                )}
                whitelabelClasses="border-[color:var(--gray-5)]"
              >
                <motion.div
                  className="relative"
                  variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }}
                >
                  <Flex
                    sharedClasses="flex flex-col md:flex-row items-start md:items-center justify-between gap-2"
                    whitelabelClasses={
                      activeShowings.length > 1
                        ? "hover:bg-[color:var(--gray-a2)]"
                        : ""
                    }
                    shopsiteClasses={
                      activeShowings.length > 1 ? "hover:bg-gray-50" : ""
                    }
                  >
                    <AccordionTrigger
                      data-testid={`eventlist-${showing.id}`}
                      disabled={activeShowings.length === 1 || !isSelectable}
                      onClick={() => {
                        createBasket();
                      }}
                      sharedClasses="relative group flex w-full justify-between px-4 py-5 sm:px-6 gap-2 flex-grow"
                      shopsiteClasses={joinClassNames(
                        "gap-x-6",
                        activeShowings.length > 1 ? "disabled:bg-gray-100" : ""
                      )}
                      whitelabelClasses={
                        activeShowings.length > 1
                          ? "disabled:bg-[color:var(--gray-a2)]"
                          : ""
                      }
                    >
                      <div
                        className={joinClassNames(
                          "relative flex items-start w-full justify-between",
                          isShopsite ? "gap-x-4" : "gap-2"
                        )}
                      >
                        <div className="flex items-start gap-x-4">
                          <div className="relative flex items-center justify-center w-8 h-5">
                            <CalendarIcon
                              className={joinClassNames(
                                "h-6 w-6 flex-none rounded bg-transparent",
                                isShopsite ? "" : "text-[color:var(--gray-11)]"
                              )}
                            />
                            {showing.status.id === ShowingStatus.SOLDOUT ||
                            showing.status.id === ShowingStatus.ADMINSOLDOUT ? (
                              <span
                                style={{ fontSize: "0.5rem" }}
                                className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 leading-none bg-red-400 text-white pt-0.5 pb-px px-0.5 w-max -rotate-6"
                              >
                                {t("soldout")}
                              </span>
                            ) : showing.access.type === AccessType.PASSWORD ? (
                              <LockClosedIcon
                                className={joinClassNames(
                                  "absolute -bottom-1 -right-1 h-4 w-4 stroke-2 text-yellow-500",
                                  isShopsite
                                    ? "bg-white"
                                    : "bg-[color:var(--gray-2)] group-hover:bg-[color:var(--gray-3)]"
                                )}
                              />
                            ) : showing.status.hot ? (
                              <FireIcon
                                className={joinClassNames(
                                  "absolute -bottom-1 -right-1 h-4 w-4 stroke-2 text-orange-400",
                                  isShopsite
                                    ? "bg-white group-hover:bg-gray-50"
                                    : "bg-[color:var(--gray-2)] group-hover:bg-[color:var(--gray-3)]"
                                )}
                              />
                            ) : (
                              <></>
                            )}
                          </div>
                          <div className="min-w-0 flex-auto text-left">
                            <Text
                              as="p"
                              size="2"
                              weight="bold"
                              shopsiteClasses="text-sm font-semibold"
                            >
                              {showing.date.display
                                ? showing.date.display
                                : showing.date.local.formatted}
                            </Text>
                            {showing.name ? (
                              <Text
                                as="p"
                                size="2"
                                weight="bold"
                                sharedClasses="w-fit mt-1 h-4"
                                whitelabelClasses="text-[color:var(--accent-11)]"
                                shopsiteClasses="text-xs leading-5 text-gray-500"
                              >
                                {showing.name}
                              </Text>
                            ) : (
                              <></>
                            )}
                          </div>
                        </div>
                        <AnimatePresence>
                          {!!bookedTickets && (
                            <motion.div
                              initial="hide"
                              animate="show"
                              exit="hide"
                              variants={{
                                hide: {
                                  opacity: 0,
                                },
                                show: {
                                  scale: [0, 1.2, 1],
                                  opacity: 1,
                                  transition: {
                                    scale: {
                                      type: "spring",
                                      duration: 0.4,
                                    },
                                  },
                                },
                              }}
                              className="flex h-5 shrink-0"
                            >
                              <Badge shopsiteClasses="!py-1 !rounded-lg">
                                <Text
                                  size="1"
                                  weight="medium"
                                  shopsiteClasses="!text-xs"
                                >
                                  {t("amountincart", {
                                    amount: bookedTickets,
                                  })}
                                </Text>
                              </Badge>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </AccordionTrigger>
                  </Flex>
                  <AccordionContent>
                    <div className="p-4">
                      {requestingBasket ? (
                        <Card>
                          <div className="flex flex-col items-center justify-center w-full h-[197px]">
                            <div className="relative mx-4">
                              <Heading
                                as="h3"
                                size="6"
                                weight="bold"
                                align="center"
                                sharedClasses="relative z-10"
                                shopsiteClasses="text-xl font-semibold text-gray-600"
                              >
                                {t("error.retrymessage")}
                              </Heading>
                            </div>
                          </div>
                        </Card>
                      ) : showing.config.showingcomboconfig &&
                        showing.config.showingcomboconfig.connectedShowings
                          .length >=
                          showing.config.showingcomboconfig.minShowings ? (
                        <ConnectedShowings
                          showingcomboconfig={showing.config.showingcomboconfig}
                          activeShowings={activeShowings}
                        >
                          {(selectedConnectedShowings) => (
                            <EventAreaPicker
                              eventId={eventId}
                              eventName={event.name}
                              eventVenueId={eventVenueId}
                              orgUid={event.organisation.uid}
                              currency={event.currency}
                              showing={showing}
                              messages={showingData?.messages ?? []}
                              basketEvent={basketEvent}
                              getPriceRange={getPriceRange}
                              pricesection={
                                event.pricing.priceschemes?.find(
                                  (ps) =>
                                    ps.id === showing.config.pricescheme.id
                                )?.pricesections
                              }
                              lastAreaSelected={lastAreaSelected}
                              setLastAreaSelected={setLastAreaSelected}
                              createBasket={createBasket}
                              selectedConnectedShowings={
                                selectedConnectedShowings
                              }
                              analyticsConfigs={
                                event.analyticsConfigs &&
                                (!cookieConsentRequired ||
                                  consent.marketing === true)
                                  ? event.analyticsConfigs
                                  : []
                              }
                              landingPages={event.landingpages}
                            />
                          )}
                        </ConnectedShowings>
                      ) : (
                        <EventAreaPicker
                          eventId={eventId}
                          eventName={event.name}
                          eventVenueId={eventVenueId}
                          orgUid={event.organisation.uid}
                          currency={event.currency}
                          showing={showing}
                          messages={showingData?.messages ?? []}
                          basketEvent={basketEvent}
                          getPriceRange={getPriceRange}
                          pricesection={
                            event.pricing.priceschemes?.find(
                              (ps) => ps.id === showing.config.pricescheme.id
                            )?.pricesections
                          }
                          lastAreaSelected={lastAreaSelected}
                          setLastAreaSelected={setLastAreaSelected}
                          createBasket={createBasket}
                          selectedConnectedShowings={null}
                          analyticsConfigs={
                            event.analyticsConfigs &&
                            (!cookieConsentRequired ||
                              consent.marketing === true)
                              ? event.analyticsConfigs
                              : []
                          }
                          landingPages={event.landingpages}
                        />
                      )}
                    </div>
                  </AccordionContent>
                </motion.div>
              </AccordionItem>
            );
          })}
        </motion.div>
      </Accordion>
    </div>
  ) : isLoading ? (
    <SkeletonLoading />
  ) : activeShowings && activeShowings.length === 0 ? (
    <div
      className={joinClassNames(
        "relative overflow-hidden",
        isShopsite ? "mt-4 sm:mt-8 border border-gray-200 rounded-lg" : "mt-8"
      )}
    >
      <div className="flex flex-col items-center justify-center w-full h-[197px]">
        <div className="relative">
          <Heading
            as="h2"
            size="8"
            weight="bold"
            sharedClasses="relative z-10"
            shopsiteClasses="font-black text-4xl"
          >
            {t("error.ohno")}
          </Heading>
          <ExclamationTriangleIcon
            className={joinClassNames(
              "absolute -top-16 -left-16 w-24 h-24 -rotate-12",
              isShopsite ? "text-red-400" : "text-[color:var(--red-a6)]"
            )}
          />
        </div>
        <div className="relative mx-4">
          <Heading
            as="h3"
            size="6"
            weight="bold"
            align="center"
            sharedClasses="relative z-10"
            shopsiteClasses="font-bold text-2xl text-center"
          >
            {t("error.noticketsmessage")}
          </Heading>
          <FaceFrownIcon
            className={joinClassNames(
              "absolute -bottom-12 -right-8 sm:-right-[4.25rem] w-24 h-24 rotate-[15deg]",
              isShopsite ? "text-[#F9CF47]" : "text-[color:var(--yellow-a6)]"
            )}
          />
        </div>
        <Text
          as="p"
          size="4"
          color="gray"
          shopsiteClasses="text-gray-500 text-lg"
        >
          {t("error.tryagain")}
        </Text>
      </div>
    </div>
  ) : (
    <div
      className={joinClassNames(
        "relative overflow-hidden",
        isShopsite ? "mt-4 sm:mt-8 border border-gray-200 rounded-lg" : "mt-8"
      )}
    >
      <div className="flex flex-col items-center justify-center w-full h-[197px]">
        <div className="relative mx-4">
          <Heading
            as="h3"
            size="6"
            weight="bold"
            sharedClasses="text-center"
            shopsiteClasses="font-bold text-2xl"
          >
            {t("error.nodatesmessage")}
          </Heading>
        </div>
        <Text
          as="p"
          color="gray"
          sharedClasses="text-lg"
          shopsiteClasses="text-gray-500"
        >
          {t("error.tryagain")}
        </Text>
      </div>
    </div>
  );
};

export default EventListMode;
